import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Hero.css';
import terraCoin from "../assets/TerraCoin.webp";
import terraCoinGold from "../assets/TerraCoinGold.webp";

const Hero = () => {
  const heroRef = useRef();
  const statsRef = useRef([]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Animate hero elements
    const tl = gsap.timeline();

    tl.fromTo('.hero-title .title-line',
      { y: 100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.2, ease: "power3.out" }
    )
      .fromTo('.hero-subtitle',
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" }, "-=0.3"
      )
      .fromTo('.hero-buttons',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: "power2.out" }, "-=0.2"
      );

    // Counter animation
    const animateCounter = (element, target) => {
      gsap.fromTo(element,
        { textContent: 0 },
        {
          textContent: target,
          duration: 2,
          ease: "power2.out",
          snap: { textContent: 1 },
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            once: true
          }
        }
      );
    };

    statsRef.current.forEach((stat) => {
      if (stat) {
        const target = parseInt(stat.dataset.count);
        animateCounter(stat, target);
      }
    });

    // Parallax effect
    gsap.to('.token-showcase', {
      yPercent: -50,
      ease: "none",
      scrollTrigger: {
        trigger: '.hero',
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section id="home" className="hero" ref={heroRef}>
      <div className="hero-bg">
        <div className="particles-canvas"></div>
        <div className="gradient-overlay"></div>
      </div>

      <div className="hero-content">
        <div className="hero-text">
          <motion.h1 className="hero-title" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <span className="title-line">The Future of</span>
            <span className="title-line gradient-text">Decentralized Finance</span>
            <span className="title-line">Starts Here</span>
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            TerraCrypt revolutionizes blockchain technology with cutting-edge security,
            lightning-fast transactions, and community-driven governance.
          </motion.p>

          <motion.div className="hero-stats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}>
            <div className="stat-item">
              <div className="stat-number" data-count="1000000" ref={el => statsRef.current[0] = el}>0</div>
              <div className="stat-label">Tokens Staked</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="50000" ref={el => statsRef.current[1] = el}>0</div>
              <div className="stat-label">Community Members</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="99" ref={el => statsRef.current[2] = el}>0</div>
              <div className="stat-label">% Uptime</div>
            </div>
          </motion.div>

          <motion.div className="hero-buttons" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.6 }}>
            <motion.button className="cta-primary" whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0, 212, 255, 0.3)" }} whileTap={{ scale: 0.95 }}>
              <span>Buy TerraCrypt</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.button>

            <motion.button className="cta-secondary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <span>View Whitepaper</span>
            </motion.button>
          </motion.div>
        </div>

        <motion.div className="hero-visual" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 1, ease: "easeOut" }}>
          <div className="token-showcase">
            <motion.img
              src={terraCoin}
              alt="TerraCrypt Token"
              className="main-token"
              animate={{ rotateY: [0, 360], scale: [1, 1.1, 1] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            />
            <motion.img
              src={terraCoinGold}
              alt="TerraCrypt Gold"
              className="floating-token gold"
              animate={{ y: [-20, 20, -20], rotateZ: [0, 180, 360] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="orbit-ring"></div>
            <div className="orbit-ring ring-2"></div>
            <div className="energy-field"></div>
          </div>
        </motion.div>
      </div>

      <motion.div className="scroll-indicator" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2, duration: 0.8 }}>
        <motion.div className="scroll-arrow" animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}></motion.div>
        <span>Scroll to explore</span>
      </motion.div>
    </section>
  );
};

export default Hero;
